import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import { useCreateCaseUpload } from '../queries';
import { useGetAllWorkflowsQuery } from '@frontend/features/workflows/queries';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { QueryKeys } from '@frontend/features/common/types';

interface CreateCaseUploadModalProps {
  open: boolean;
  onClose: () => void;
  agencyId: string;
}

export const CreateCaseUploadModal = ({ open, onClose, agencyId }: CreateCaseUploadModalProps) => {
  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState<string>('');

  const { data: workflows } = useGetAllWorkflowsQuery({
    agencyId: agencyId,
    pageSize: 1000,
    currentPage: 1,
    search:'',
  });

  const { mutateAsync: createCaseUpload } = useCreateCaseUpload({
    onSuccess: () => {
      onClose();
      setSelectedFile(null);
      setSelectedWorkflow('');
      queryClient.invalidateQueries([QueryKeys.getPaginatedCaseUploads, agencyId]);
      addToast({
        message: t('dataUploads.cases.create.createdSuccess'),
        color: 'success',
      });
    },
    onError: () => {
      addToast({
        message: t('dataUploads.cases.create.createdError'),
        color: 'error',
      });
    },
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleWorkflowChange = (event: any) => {
    setSelectedWorkflow(event.target.value);
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedWorkflow) return;

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('agencyId', agencyId);
    formData.append('workflowId', selectedWorkflow);

    try {
      await createCaseUpload(formData);
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="xs" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '8px',
          p: 1
        }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, fontSize: '1.1rem' }}>
        {t('dataUploads.cases.create.title')}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 0 }}>
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="workflow-select-label">{t('cases.view.workflow')}</InputLabel>
          <Select
            labelId="workflow-select-label"
            value={selectedWorkflow}
            onChange={handleWorkflowChange}
            label={t('cases.view.workflow')}
          >
            {workflows?.items.map((workflow) => (
              <MenuItem key={workflow.id} value={workflow.id}>
                {workflow.name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {t('dataUploads.cases.create.selectWorkflow')}
          </FormHelperText>
        </FormControl>

        <Box sx={{ 
          textAlign: 'center', 
          py: 4,
          px: 2,
          border: '1px dashed',
          borderColor: 'primary.main',
          borderRadius: 1,
          bgcolor: (theme) => theme.palette.grey[50],
          cursor: 'pointer'
        }}>
          <Box component="label" sx={{ cursor: 'pointer', display: 'block' }}>
            <input
              type="file"
              hidden
              accept=".csv"
              onChange={handleFileSelect}
            />
            <CloudUploadIcon 
              sx={{ 
                fontSize: 40, 
                color: 'primary.main',
                opacity: 0.8
              }} 
            />
            <Box sx={{ 
              color: 'primary.main',
              mt: 1,
              fontSize: '0.875rem'
            }}>
              {selectedFile ? selectedFile.name : t('dataUploads.cases.create.description')}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button onClick={onClose} variant="text">
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={!selectedFile || !selectedWorkflow}
          disableElevation
        >
          {t('common.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 
